import { useCallback, useEffect, useState } from 'react';
import { Spin, Steps } from 'antd';
import { Icon } from 'Components';
import Step1 from '../Step1';
import Step2TestConfiguration from '../Step2';
import Step4DeliverySchedule from '../Step4';
import Step3Recipients from '../Step3';
import Step5ReviewStart from '../Step5';
import { CampaignThresholdData } from '../..';
import ExitCampaignModal from './components/ExitCampaignModal';
import {
	CreateCampaignStepOnePayload,
	createPhishingCampaignStepOne,
	createPhishingCampaignStepTwo,
	getPhishingCampaignFilters,
	getPhishingCampaignTemplatePreviewDetails,
	launchPhishingCampaign,
	discardPhishingCampaign
} from '../../service/index.Service';
import PhishingModal from './components/PhishingModal';
import Header from '../../components/Header';
import { DESKTOP_STEPS, MOBILE_STEPS } from '../../PhishingManager.constants';
import { errorMessageHandler } from '../../utils';
const { Step } = Steps;

type CampaignSetupProps = {
	tenantId: number | string | undefined;
	onBack: () => void;
	thresholdData: null | CampaignThresholdData;
	errorMessage?: string;
	setToast: (
		toast: { message: string; type: 'warning' | 'success' } | null
	) => void;
	isMobile: boolean;
	toggleCampaignCompletePopUp: (value: 'success' | 'error') => void;
};

export interface FilterResults {
	languages: { id: number; name: string }[];
	difficulties: string[];
	categories: { id: number; name: string }[];
}

export interface TemplateLanguage {
	id: number;
	name?: string;
}

export interface TemplateDetails {
	id: number;
	name: string;
	categories: { id: number; name: string }[];
	description: string | null;
	difficulty: 'beginner' | 'intermediate' | 'advanced' | string;
	email_template_html: string;
	landing_page_html: string;
	image_path: string;
	selected_packaged_template_language_id: number;
	languages: TemplateLanguage[];
}

export default function CampaignSetup({
	onBack,
	tenantId,
	thresholdData,
	setToast,
	isMobile,
	toggleCampaignCompletePopUp
}: CampaignSetupProps) {
	const [currentStep, setCurrentStep] = useState(0);
	const [showPhisingModal, setShowPhisingModal] = useState<boolean>(false);
	const [selectedTemplate, setSelectedTemplate] = useState<
		string | number | null
	>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [showExitModal, setShowExitModal] = useState(false);
	const [campaignId, setCampaignId] = useState<string | number | null>(null);
	const [filterResults, setFilterResults] = useState<FilterResults | null>(
		null
	);
	const [
		templateDetails,
		setTemplateDetails
	] = useState<TemplateDetails | null>(null);
	const STEPS = isMobile ? MOBILE_STEPS : DESKTOP_STEPS;
	const toggleExitModal = useCallback(
		(show: boolean) => setShowExitModal(show),
		[]
	);
	const [step1State, setStep1State] = useState({
		search: '',
		language: '',
		category: '',
		difficulty: '',
		templatePage: 1
	});
	const [stepErrorMessage, setStepErrorMessage] = useState<string | null>(
		null
	);
	const [showConfirmBtn, setShowConfirmBtn] = useState(true);

	const handleNext = async () => {
		if (currentStep > 1) {
			setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
			return;
		}
		if (
			!tenantId ||
			!templateDetails?.id ||
			!templateDetails?.selected_packaged_template_language_id
		)
			return;
		setIsLoading(true);
		try {
			const payload: CreateCampaignStepOnePayload = {
				venza_tenant_id: Number(tenantId),
				packaged_template_id: Number(templateDetails?.id),
				language_id: Number(
					templateDetails?.selected_packaged_template_language_id
				)
			};
			if (!!campaignId) {
				payload['campaign_id'] = Number(campaignId);
			}
			const response = await createPhishingCampaignStepOne(payload);
			setCampaignId(response.data.campaign_id);
			setCurrentStep((s) => Math.min(s + 1, 4));
		} catch (err: any) {
			const errorMessage = errorMessageHandler(
				err,
				'Failed to create campaign step 2'
			);
			setTemplateDetails(null);
			setSelectedTemplate(null);
			setToast({ message: errorMessage, type: 'warning' });
		} finally {
			setIsLoading(false);
		}
	};

	const handlePhishingModalClose = async () => {
		setShowPhisingModal(false);
		setSelectedTemplate(null);
		setShowConfirmBtn(true);
	};

	const resetCampaignState = () => {
		setCurrentStep(0);
		setSelectedTemplate(null);
		setCampaignId(null);
		setTemplateDetails(null);
		setFilterResults(null);
		setShowPhisingModal(false);
		setShowExitModal(false);
		setStep1State({
			search: '',
			language: '',
			category: '',
			difficulty: '',
			templatePage: 1
		});
		onBack();
	};

	const handleDiscardCampaign = async () => {
		if (!tenantId || !campaignId) {
			setToast({
				message:
					'Unable to discard campaign: Missing tenant or campaign ID',
				type: 'warning'
			});
			return;
		}
		setIsLoading(true);
		setShowExitModal(false);
		try {
			const result = await discardPhishingCampaign({
				venza_tenant_id: Number(tenantId),
				campaign_id: Number(campaignId)
			});
			setToast({ message: result.message, type: 'success' });
			resetCampaignState();
		} catch (err: any) {
			const errorMessage = errorMessageHandler(
				err,
				'Failed to discard campaign'
			);
			setToast({ message: errorMessage, type: 'warning' });
		} finally {
			setIsLoading(false);
		}
	};

	const handleExitConfirm = async () => {
		if (campaignId) {
			await handleDiscardCampaign();
		} else {
			onBack();
		}
	};

	const handleLaunchCampaign = async () => {
		if (!tenantId || !campaignId) {
			setToast({
				message: 'Unable to launch campaign: Missing required data',
				type: 'warning'
			});
			return;
		}
		setIsLoading(true);
		try {
			const result = await launchPhishingCampaign({
				venza_tenant_id: Number(tenantId),
				campaign_id: Number(campaignId)
			});
			setToast({ message: result.message, type: 'success' });
			toggleCampaignCompletePopUp('success');
		} catch (err: any) {
			const errorMessage = errorMessageHandler(
				err,
				'Failed to launch campaign'
			);
			toggleCampaignCompletePopUp('error');
			setToast({ message: errorMessage, type: 'warning' });
		} finally {
			resetCampaignState();
			setIsLoading(false);
		}
	};

	const handleNext2 = async (emails: string[]) => {
		if (!tenantId || !templateDetails?.id || !emails?.length) return;
		setIsLoading(true);
		try {
			await createPhishingCampaignStepTwo({
				venza_tenant_id: Number(tenantId),
				campaign_id: Number(campaignId),
				emails: emails
			});
			setCurrentStep((s) => Math.min(s + 1, 4));
		} catch (err: any) {
			const errorMessage = errorMessageHandler(
				err,
				'Failed to create campaign step 2'
			);
			setStepErrorMessage(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleBack = () => {
		if (currentStep == 1) setSelectedTemplate(null);
		setCurrentStep((s) => Math.max(s - 1, 0));
	};

	const handlePreviewTemplate = async (id: string | number | null) => {
		if (!!id) {
			setSelectedTemplate(id);
			try {
				setShowPhisingModal(true);
				if (id == templateDetails?.id) return;
				setTemplateDetails(null);
				setIsLoading(true);
				const response = await getPhishingCampaignTemplatePreviewDetails(
					tenantId,
					id ?? ''
				);
				setTemplateDetails(response.data);
			} catch (err: any) {
				const errorMessage = errorMessageHandler(
					err,
					'Failed to get preview template details'
				);
				setToast({ message: errorMessage, type: 'warning' });
				setShowPhisingModal(false);
				setSelectedTemplate(null);
			} finally {
				setIsLoading(false);
			}
		} else {
			setShowPhisingModal(false);
			setTemplateDetails(null);
			setSelectedTemplate(null);
		}
	};

	const loadPreviewDetails = async (langId: string | number | null) => {
		setIsLoading(true);
		try {
			const response = await getPhishingCampaignTemplatePreviewDetails(
				tenantId,
				templateDetails?.id ?? '',
				langId ?? ''
			);
			setTemplateDetails(response.data);
		} catch (err: any) {
			const errorMessage = errorMessageHandler(
				err,
				'Unable to load preview details'
			);
			setToast({ message: errorMessage, type: 'warning' });
			setTemplateDetails(null);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		const fetchFilterCampaigns = async (value?: string) => {
			setIsLoading(true);
			try {
				const response = await getPhishingCampaignFilters(
					tenantId,
					value ?? ''
				);
				setFilterResults(response.data);
			} catch (error: any) {
				setFilterResults(null);
			} finally {
				setIsLoading(false);
			}
		};
		fetchFilterCampaigns();
	}, []);

	return (
		<Spin spinning={isLoading}>
			<div className="campaign-step-up">
				<Header
					title="Campaign setup"
					description="Follow the steps to create a new campaign"
				>
					<div
						className="close-icon-wrapper"
						onClick={() => toggleExitModal(true)}
					>
						<Icon type="campaignSetupClose" />
					</div>
				</Header>

				<div className="campaign-step-track-container">
					<div className="phishing-step1">
						<Steps
							current={currentStep}
							className="mx-auto d-flex"
							size="small"
							labelPlacement="vertical"
						>
							{STEPS.map((label, i) => (
								<Step
									key={i}
									status={
										i < currentStep
											? 'finish'
											: i === currentStep
											? 'process'
											: 'wait'
									}
									className="d-flex justify-center"
									style={{
										cursor:
											i <= currentStep
												? 'pointer'
												: 'default'
									}}
									title={label}
								/>
							))}
						</Steps>
					</div>
				</div>

				<div className="step-card-section">
					<div
						style={{
							display: currentStep === 0 ? 'block' : 'none',
							width: '100%'
						}}
					>
						<Step1
							{...{
								isMobile,
								tenantId,
								step1State,
								setStep1State,
								setToast,
								filterResults,
								thresholdData,
								selectedTemplate
							}}
							onSelect={handlePreviewTemplate}
							onNext={handleNext}
							onClose={onBack}
						/>
					</div>

					<div
						style={{
							display: currentStep === 1 ? 'block' : 'none',
							width: '100%'
						}}
					>
						<Step2TestConfiguration
							setStepErrorMessage={setStepErrorMessage}
							stepErrorMessage={stepErrorMessage}
							onBack={handleBack}
							onNext={handleNext2}
						/>
					</div>

					<div
						style={{
							display: currentStep === 2 ? 'block' : 'none',
							width: '100%'
						}}
					>
						<Step3Recipients
							{...{ isMobile }}
							onBack={handleBack}
							onNext={handleNext}
						/>
					</div>

					<div
						style={{
							display: currentStep === 3 ? 'block' : 'none',
							width: '100%'
						}}
					>
						<Step4DeliverySchedule
							{...{ tenantId, campaignId, setToast }}
							onBack={handleBack}
							onNext={handleNext}
						/>
					</div>

					{currentStep === 4 && (
						<div style={{ width: '100%' }}>
							<Step5ReviewStart
								{...{ tenantId, campaignId, setToast }}
								onBack={handleBack}
								onFinish={handleLaunchCampaign}
								showPreview={(id: number | string) => {
									setShowConfirmBtn(false);
									handlePreviewTemplate(id);
								}}
							/>
						</div>
					)}
				</div>
			</div>
			<ExitCampaignModal
				open={showExitModal}
				onClose={() => toggleExitModal(false)}
				onConfirm={handleExitConfirm}
			/>
			<PhishingModal
				showConfirmBtn={showConfirmBtn}
				loading={isLoading}
				visible={showPhisingModal}
				templateDetails={templateDetails}
				onClose={handlePhishingModalClose}
				onNext={() => {
					handleNext();
					setShowPhisingModal(false);
				}}
				onLanguageChange={(langId: number) => {
					loadPreviewDetails(langId);
				}}
			/>
		</Spin>
	);
}
